import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import to from 'await-to-js'
import { compact, uniqBy } from 'lodash-es'
import { CircleCheckIcon, CloudUploadIcon } from 'lucide-react'
import { DropzoneOptions } from 'react-dropzone'
import { toast } from 'sonner'

import { uploadFileWithProgress } from '@/lib/upload.ts'
import { useModalState } from '@/hooks/use-modal-state.ts'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button.tsx'
import {
  FileInput,
  FileUploader,
  FileUploaderContent,
} from '@/components/ui/extension/file-upload.tsx'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import { Breadcrumbs } from '@/components/layout/breadcrumbs.tsx'
import { PageTitle } from '@/components/layout/page-title.tsx'
import { apiClient, formatClientResponse } from '@/apiClient.ts'

import { DocumentUploadItem } from './-components/document-upload-item.tsx'
import { UploadingFile } from './-components/types.ts'

type LocalFile = File

export const Route = createFileRoute('/_authenticated/documents/upload/')({
  component: Documents,
})

const dropZoneConfig: DropzoneOptions = {
  maxFiles: 5,
  maxSize: 1024 * 1024 * 50,
  multiple: true,
  accept: { 'application/pdf': ['.pdf'] },
}

function getFileFingerprint(file: LocalFile): string {
  return compact([
    file.name,
    file.size,
    file.type,
    file.lastModified,
    file.webkitRelativePath,
  ]).join('-')
}

function Documents() {
  const navigate = Route.useNavigate()
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([
    // {
    //   status: 'uploaded',
    //   file: { name: 'Qualcosa' },
    //   progress: 100,
    // },
  ])

  const updateFileItem = (
    fingerprint: string,
    updatedFile: Partial<UploadingFile>
  ) => {
    setUploadingFiles((prev) =>
      prev.map((uf) =>
        uf.fingerprint === fingerprint ? { ...uf, ...updatedFile } : uf
      )
    )
  }

  const handleFileSelection = (value: LocalFile[] | null) => {
    const newFiles = uniqBy(value || [], getFileFingerprint)
    const newFingerprints = newFiles.map(getFileFingerprint)
    const existingFingerprints = uploadingFiles.map((uf) => uf.fingerprint)

    const removedFingerprints = existingFingerprints.filter(
      (fingerprint) => !newFingerprints.includes(fingerprint)
    )

    removedFingerprints.forEach((fingerprint) => {
      const uf = uploadingFiles.find((uf) => uf.fingerprint === fingerprint)
      uf?.cancelUpload?.()
    })

    setUploadingFiles((prev) =>
      prev.filter((uf) => !removedFingerprints.includes(uf.fingerprint))
    )

    const addedFiles = newFiles.filter(
      (file) => !existingFingerprints.includes(getFileFingerprint(file))
    )

    addedFiles.forEach((file) => {
      startUpload(file)
    })
  }

  const startUpload = async (file: LocalFile) => {
    const fingerprint = getFileFingerprint(file)

    // Add file to state immediately with a temporary ID
    setUploadingFiles((prev) => [
      ...prev,
      {
        file,
        fingerprint,
        progress: 0,
        status: 'idle',
        fileId: `temp-${fingerprint}`,
        cancelUpload: () => {}, // Will be replaced with actual cancel function
      },
    ])

    const [error, response] = await to(
      apiClient.uploads.signature
        .$get({ query: { name: file.name, mimeType: 'application/pdf' } })
        .then((r) => formatClientResponse(r))
    )

    if (error) {
      toast.error(`Si è verificato un errore durante l'upload di ${file.name}`)
      updateFileItem(fingerprint, { status: 'error' })
      return
    }

    const { promise, cancelUpload } = uploadFileWithProgress({
      file,
      signedUrl: response.url,
      onUploaded: async () => {
        await apiClient.files[':fileId'].uploaded.$put({
          param: { fileId: response.fileId },
        })

        const result = await apiClient.documents
          .$post({
            json: { name: file.name, file_id: response.fileId },
          })
          .then((r) => formatClientResponse(r))

        updateFileItem(fingerprint, { documentId: result.data.id })
      },
      onProgress: (progressEvent) => {
        updateFileItem(fingerprint, {
          status: 'uploading',
          progress: progressEvent.percentage,
        })
      },
      onError: () => {
        toast.error(
          `Si è verificato un errore durante l'upload di ${file.name}`
        )
        updateFileItem(fingerprint, { status: 'error' })
      },
    })

    // Update with real fileId and cancel function
    updateFileItem(fingerprint, {
      fileId: response.fileId,
      cancelUpload,
    })

    await promise

    updateFileItem(fingerprint, { status: 'uploaded' })
  }

  const startAnalysisMutation = useMutation({
    mutationKey: ['start-uploads-analysis', uploadingFiles],
    mutationFn: async () => {
      await apiClient['document-analysis'].$post({
        json: {
          items: uploadingFiles.map((uf) => ({
            documentId: uf.documentId!,
            attributeGroupId: uf.attributeGroupId,
          })),
        },
      })
    },
  })
  const analysisAlertModal = useModalState()
  const handleStartAnalysis = async ({
    force = false,
  }: {
    force?: boolean
  }) => {
    if (!force && uploadingFiles.some((uf) => !uf.attributeGroupId)) {
      analysisAlertModal.open()
      return
    }

    await startAnalysisMutation.mutateAsync()
    toast('Analisi iniziata', { icon: <CircleCheckIcon /> })
    navigate({ to: '/documents/processing' })
  }

  const isStartAnalysisBtnEnabled = useMemo(() => {
    if (startAnalysisMutation.isPending) return false

    return uploadingFiles.every(
      (uf) => uf.status == 'uploaded' && uf.documentId
    )
  }, [startAnalysisMutation.isPending, uploadingFiles])

  return (
    <ScrollArea className="p-4">
      <Breadcrumbs
        items={[
          { name: 'Documenti', to: '/documents/browse' },
          { name: 'Upload' },
        ]}
      />
      <PageTitle>Upload documenti</PageTitle>

      <div className="max-w-screen-sm">
        <FileUploader
          value={uploadingFiles.map((uf) => uf.file)}
          onValueChange={handleFileSelection}
          dropzoneOptions={dropZoneConfig}
        >
          <div className="bg-background relative mb-6 rounded-lg border border-dashed p-2 shadow-sm">
            <FileInput className="outline-dashed outline-1 outline-white">
              <div className="flex w-full flex-col items-center justify-center pb-4 pt-3">
                <CloudUploadIcon className="mb-3 size-8 text-gray-500 dark:text-gray-400" />
                <p className="mb-1 text-center text-sm text-gray-500 dark:text-gray-400">
                  Clicca qui <br />o trascina i file
                </p>
              </div>
            </FileInput>
          </div>

          <FileUploaderContent className="-mx-1 mb-2">
            {uploadingFiles.map((uf, i) => (
              <DocumentUploadItem
                key={uf.fingerprint}
                index={i}
                uploadingFile={uf}
                onChange={(file) => updateFileItem(file.fingerprint, file)}
              />
            ))}
          </FileUploaderContent>
        </FileUploader>
        <div className="mt-8 flex justify-between">
          <span></span>
          {uploadingFiles.length > 0 && (
            <Button
              onClick={() => handleStartAnalysis({ force: false })}
              variant="default"
              disabled={!isStartAnalysisBtnEnabled}
            >
              Inizia analisi testo
            </Button>
          )}
        </div>
      </div>

      <AlertDialog
        open={analysisAlertModal.isOpen}
        onOpenChange={(isOpen) => analysisAlertModal.setOpen(isOpen)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Non hai selezionato il gruppo attributi per alcuni documenti
            </AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler continuare?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={startAnalysisMutation.isPending}
              onClick={() => handleStartAnalysis({ force: true })}
            >
              Continua
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ScrollArea>
  )
}
